"use client";
import { motion, AnimatePresence } from "framer-motion";
import { useState, useRef, useEffect } from "react";
import { User, Settings, LogOut, ChevronDown, Crown } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useAuth } from "@/lib/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

export default function UserMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { user, logout } = useAuth();
  const router = useRouter();

  const displayName = user?.preferredName || user?.name || "User";
  const initials = displayName
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  const subtitle = user?.assignedCourse
    ? user.assignedCourse
    : user?.role === "admin"
      ? "Admin"
      : user?.role === "course_rep"
        ? "Course Rep"
        : "Student";

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <motion.button
        whileHover={{ scale: 1.03 }}
        whileTap={{ scale: 0.97 }}
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-3 p-1.5 pr-3 rounded-xl hover:bg-[var(--glass-bg)] transition-colors"
      >
        <Avatar className="w-8 h-8 rounded-xl shadow-lg no-white-force">
          <AvatarImage src={user?.avatar ?? undefined} alt={displayName} />
          <AvatarFallback className="bg-gradient-to-br from-y to-y/60 text-b text-xs font-black">
            {initials}
          </AvatarFallback>
        </Avatar>
        <div className="hidden md:block text-left">
          <p className="text-xs font-bold text-foreground leading-none">
            {displayName}
          </p>
          <p className="text-xs text-[var(--text-muted)] font-medium mt-0.5">
            {subtitle}
          </p>
        </div>
        <ChevronDown
          className={`w-4 h-4 text-[var(--text-muted)] transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ type: "spring", bounce: 0.2, duration: 0.3 }}
            style={{
              position: "absolute",
              right: 0,
              top: "100%",
              marginTop: 8,
              maxWidth: "none",
              zIndex: 150,
            }}
            className="w-56 glass border border-y/20 rounded-2xl shadow-2xl overflow-hidden sm:origin-top-right"
          >
            <div className="p-2 space-y-0.5">
              <Link
                href={`/u/${user?.username || user?.id || ""}`}
                className="w-full"
              >
                <button className="w-full flex items-center gap-3 px-3 py-2.5 rounded-full hover:bg-muted/20 transition-colors group">
                  <User className="w-4 h-4 text-muted-foreground/40 group-hover:text-y transition-colors" />
                  <span className="text-sm text-foreground group-hover:text-foreground transition-colors flex-1">
                    View Profile
                  </span>
                </button>
              </Link>
              <Link href="/settings" className="w-full">
                <button className="w-full flex items-center gap-3 px-3 py-2.5 rounded-full hover:bg-muted/20 transition-colors group">
                  <Settings className="w-4 h-4 text-muted-foreground/40 group-hover:text-y transition-colors" />
                  <span className="text-sm text-foreground">Settings</span>
                </button>
              </Link>
              <Link href="/pay" className="w-full">
                <button className="w-full flex items-center gap-3 px-3 py-2.5 rounded-full hover:bg-muted/20 transition-colors group">
                  <Crown className="w-4 h-4 text-muted-foreground/40 group-hover:text-y transition-colors" />
                  <span className="text-sm text-foreground">Upgrade Plan</span>
                </button>
              </Link>
              <div className="h-px bg-muted/20 my-1" />
              <button
                onClick={async () => {
                  setIsOpen(false);
                  await logout();
                  router.replace("/auth/login");
                }}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-full hover:bg-red-500/10 transition-colors group no-white-force"
              >
                <LogOut className="w-4 h-4 text-muted-foreground/40 group-hover:text-red-400 transition-colors" />
                <span className="text-sm text-foreground group-hover:text-red-400 transition-colors">
                  Sign Out
                </span>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
